import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

const checklist = [
  { id: 'chars', text: 'Все игроки создали персонажей' },
  { id: 'bonds', text: 'Персонажи связаны узами (bonds)' },
  { id: 'world', text: 'Обсудили базовые детали мира' },
  { id: 'threat', text: 'Есть начальная угроза или задание' },
  { id: 'location', text: 'Подготовлена начальная локация' },
  { id: 'npcs', text: 'Есть 2-3 NPC для первой встречи' },
  { id: 'maps', text: 'Карта или хотя бы набросок' },
];

const playerQuestions = [
  {
    question: 'Как вы все познакомились?',
    purpose: 'Создаёт историю группы',
  },
  {
    question: 'Почему вы путешествуете вместе?',
    purpose: 'Определяет цель партии',
  },
  {
    question: 'Кто ваш общий враг или проблема?',
    purpose: 'Создаёт начальный конфликт',
  },
  {
    question: 'Что привело вас в это место?',
    purpose: 'Запускает приключение',
  },
  {
    question: 'У кого из вас есть секрет от остальных?',
    purpose: 'Добавляет внутреннюю драму',
  },
];

const sessionTips = [
  {
    title: 'Начните с действия',
    text: 'Не тратьте время на "вы встретились в таверне". Начните с погони, боя или загадки.',
  },
  {
    title: 'Используйте узы',
    text: 'Узы персонажей — готовые зацепки для приключений. Ваша сестра пропала? Вот и квест.',
  },
  {
    title: 'Задавайте вопросы',
    text: '"Маг, что ты знаешь о этих рунах?" — пусть игроки создают мир вместе с вами.',
  },
  {
    title: 'Делайте мягкие ходы',
    text: 'Показывайте угрозу, прежде чем она ударит. Дайте игрокам шанс среагировать.',
  },
  {
    title: 'Заканчивайте на интересном',
    text: 'Оставьте cliffhanger — пусть игроки ждут следующей сессии.',
  },
];

export function FirstSessionPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const [checked, setChecked] = useState<string[]>([]);

  const toggleCheck = (id: string) => {
    hapticFeedback('light');
    setChecked(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/gm-tools');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Первая сессия</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Checklist */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            ✅ Чеклист подготовки
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-2"
          >
            {checklist.map((item) => (
              <button
                key={item.id}
                onClick={() => toggleCheck(item.id)}
                className={`w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all ${
                  checked.includes(item.id)
                    ? 'bg-green-500/20 border border-green-500/30'
                    : 'bg-gray-800/50 border border-gray-700/50'
                }`}
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                  checked.includes(item.id)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-700'
                }`}>
                  {checked.includes(item.id) && '✓'}
                </div>
                <span className={`text-sm ${
                  checked.includes(item.id) ? 'text-green-300' : 'text-gray-300'
                }`}>
                  {item.text}
                </span>
              </button>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-500 text-sm mt-3"
          >
            Готово: {checked.length} / {checklist.length}
          </motion.p>
        </section>

        {/* Player Questions */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            ❓ Вопросы игрокам
          </motion.h2>

          <div className="space-y-3">
            {playerQuestions.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 0.05 * index }}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <p className="text-white text-sm mb-1">"{item.question}"</p>
                <p className="text-gray-400 text-xs">→ {item.purpose}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            💡 Советы для первой сессии
          </motion.h2>

          <div className="space-y-3">
            {sessionTips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 + 0.05 * index }}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <h3 className="font-medium text-white mb-1">{tip.title}</h3>
                <p className="text-gray-300 text-sm">{tip.text}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
